import { useCallback, useEffect, useState } from 'react';

interface ColumnVisibilityOptions {
  storageKey: string;
  allColumnKeys: string[];
  defaultHidden?: string[];
}

interface ColumnVisibility {
  hidden: Set<string>;
  isHidden: (key: string) => boolean;
  toggle: (key: string) => void;
  setHidden: (keys: string[]) => void;
  resetToDefaults: () => void;
}

function readStored(storageKey: string): string[] | null {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return parsed.filter((v): v is string => typeof v === 'string');
  } catch {
    return null;
  }
}

export function useColumnVisibility({
  storageKey,
  allColumnKeys,
  defaultHidden = [],
}: ColumnVisibilityOptions): ColumnVisibility {
  const [hidden, setHiddenState] = useState<Set<string>>(() => {
    const stored = readStored(storageKey);
    const initial = stored ?? defaultHidden;
    return new Set(initial.filter((k) => allColumnKeys.includes(k)));
  });

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(Array.from(hidden)));
    } catch {
      // ignore quota / unavailable storage
    }
  }, [hidden, storageKey]);

  const isHidden = useCallback((key: string) => hidden.has(key), [hidden]);

  const toggle = useCallback((key: string) => {
    setHiddenState((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const setHidden = useCallback((keys: string[]) => {
    setHiddenState(new Set(keys));
  }, []);

  const resetToDefaults = useCallback(() => {
    setHiddenState(new Set(defaultHidden));
  }, [defaultHidden]);

  return { hidden, isHidden, toggle, setHidden, resetToDefaults };
}
